// src/components/admin/Users/AgencyCustomersTab.js
import React, { useState, useEffect } from 'react';
import { 
  Transfer, Card, Spin, Alert, Typography, 
  Empty, Button, List, Avatar, Tag, Divider
} from 'antd';
import { 
  UserOutlined, TeamOutlined, SearchOutlined,
  SaveOutlined, DeleteOutlined, ReloadOutlined
} from '@ant-design/icons';
import agencyCustomerService from '../../../services/agencyCustomerService';
import userService from '../../../services/userService';
import ClientOptionLabel, { buildClientSearchText } from '../../common/ClientOptionLabel';

const { Title, Text } = Typography;

const getCustomerId = (customer) => String(customer?.id ?? customer);

const canAssignAsCustomer = (customer, agencyId) => {
  if (!customer?.id || String(customer.id) === String(agencyId)) {
    return false;
  }

  return true;
};

/**
 * Component for managing customer assignments to an agency
 */
const AgencyCustomersTab = ({ agencyId, readOnly = false, onCustomersUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [assignedCustomers, setAssignedCustomers] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [searchText, setSearchText] = useState('');

  // Fetch data when component mounts or agencyId changes
  useEffect(() => {
    console.log('Agency ID:', agencyId);
    if (agencyId) {
      fetchData();
    }
  }, [agencyId]);

  // Fetch both available and assigned customers
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch customers currently assigned to this agency
      const assignedResponse = await agencyCustomerService.getAgencyCustomers(agencyId);
      const assignedCustomerData = assignedResponse.data || [];
      setAssignedCustomers(assignedCustomerData);
      setSelectedCustomerIds(assignedCustomerData.map(getCustomerId));
      console.log('Assigned customers:', assignedCustomerData.map(customer => customer.id));
      
      // Fetch all available customers that could be assigned to this agency
      const [availableResponse, allUsersResponse] = await Promise.all([
        agencyCustomerService.getAvailableCustomers(agencyId),
        userService.getAllCustomers({ limit: 10000 })
      ]);
      //set available customers all assignedCustomerData + availableResponse.data
      const allCustomers = [
        ...assignedCustomerData,
        ...(availableResponse.data || []),
        ...((allUsersResponse.data || []).filter(customer => canAssignAsCustomer(customer, agencyId)))
      ];
      // Filter out duplicates based on customer ID
      const uniqueCustomers = Array.from(new Set(allCustomers.map(getCustomerId)))
        .map(id => allCustomers.find(customer => getCustomerId(customer) === id));
      setAvailableCustomers(uniqueCustomers);
     } catch (error) {
      console.error('Error fetching agency customers data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle transfer change (customers selected/deselected)
  const handleTransferChange = (nextTargetKeys) => {
    setSelectedCustomerIds(nextTargetKeys);
    
    // Notify parent component if provided
    if (onCustomersUpdated) {
      onCustomersUpdated(nextTargetKeys);
    }
  };

  // Handle saving customer assignments
  const handleSaveAssignments = async () => {
    try {
      setLoading(true);
      
      await agencyCustomerService.updateAgencyCustomers(agencyId, selectedCustomerIds.map(Number));
      
      // Refresh data to show updated assignments
      fetchData();
    } catch (error) {
      console.error('Error updating agency customers:', error);
    } finally {
      setLoading(false);
    }
  };

  // If no agency ID provided, show empty state
  if (!agencyId) {
    return (
      <Card>
        <Empty 
          description="No agency selected" 
          image={Empty.PRESENTED_IMAGE_SIMPLE} 
        />
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <div className="flex justify-between items-center mb-4">
        <Title level={4}>
          <TeamOutlined className="mr-2" />
          Manage Agency Customers
        </Title>
        
        {!readOnly && (
          <Button 
            type="primary" 
            onClick={handleSaveAssignments}
            loading={loading}
            icon={<SaveOutlined />}
            className="bg-golden-navy"
          >
            Save Assignments
          </Button>
        )}
      </div>
      
      <Alert
        type="info"
        showIcon
        message="Agency Customer Management"
        description="Select the customers that this agency will be able to manage. The agency will have access to these customers' properties, orders, and other data."
        className="mb-4"
      />
      
      <Spin spinning={loading}>
        {readOnly ? (
          // Read-only view: simple list of assigned customers
          <div>
            {assignedCustomers.length > 0 ? (
              <List
                itemLayout="horizontal"
                dataSource={assignedCustomers}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} />}
                      title={<Text strong>{item.name}</Text>}
                      description={
                        <div>
                          <div>{item.email}</div>
                          {item.phone && <div>{item.phone}</div>}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No customers assigned to this agency yet" />
            )}
          </div>
        ) : (
          // Edit mode: Transfer component for adding/removing customers
          <Transfer
            dataSource={availableCustomers.map(customer => ({
              key: getCustomerId(customer),
              title: customer.name || customer.company_name || customer.email || `Customer ${customer.id}`,
              description: customer.email || '',
              client: customer,
              chosen: selectedCustomerIds.includes(getCustomerId(customer))
            }))}
            titles={['Available Customers', 'Managed Customers']}
            targetKeys={selectedCustomerIds}
            onChange={handleTransferChange}
            render={item => (
              <div className="py-1">
                <ClientOptionLabel client={item.client || item} />
              </div>
            )}
            listStyle={{
              width: 350,
              height: 500,
            }}
            operations={['→', '←']}
            showSearch
            filterOption={(inputValue, item) =>
              buildClientSearchText(item.client || item).includes(inputValue.toLowerCase())
            }
          />
        )}
      </Spin>
    </Card>
  );
};

export default AgencyCustomersTab;
